// client/src/pages/adminDashboard.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import BackButton from '../../components/customs/backButton';
import { GlobalContext } from '../../context/provider';
import useRecognizedInstructor from '../../hooks/useRecognizedInstructor';
import useInstructor from '../../hooks/useInstructor';
import useAuditLog from '../../hooks/useAuditLog';
import useStudent from '../../hooks/useStudent';
import useMessage from '../../hooks/useMessage';
import useCourse from '../../hooks/useCourse';

import '../../styles/globalstyles.css';
import './adminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authState: { authData } } = useContext(GlobalContext);
  
  const { getAllCourses } = useCourse();
  const { getAllInstructors } = useInstructor();
  const { getAllStudents } = useStudent();
  const { getAllRecognizedInstructors } = useRecognizedInstructor();
  const { getUserActivity } = useAuditLog();
  const { autoRegisterFCM } = useMessage();

  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab || 'overview'
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalInstructors: 0,
    totalCourses: 0,
    activeCourses: 0,
    recognizedInstructors: 0
  });

  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [students, setStudents] = useState([]);
  const [recognizedInstructors, setRecognizedInstructors] = useState([]);
  
  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  const [pendingRefundsCount, setPendingRefundsCount] = useState(0);

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const handleNavigateToManagement = (section) => {
    navigate(`/admin/${section}`, { state: { previousTab: activeTab } });
  };

  useEffect(() => {
    loadPage();
    loadRecentActivities(); 
    autoRegisterFCM(authData);
  }, []);

  const loadPage = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [coursesRes, instructorsRes, studentsRes, recognizedRes] = await Promise.all([
        getAllCourses(),
        getAllInstructors(),
        getAllStudents(),
        getAllRecognizedInstructors()
      ]);

      if (coursesRes.success) setCourses(coursesRes.data || []);
      if (instructorsRes.success) setInstructors(instructorsRes.data || []);
      // if (studentsRes.success) setStudents(studentsRes.data || []);
      if (studentsRes.success) {
        setStudents(studentsRes.data || []);
        const allPayments = (studentsRes.data || []).flatMap(s => s.paymentHistory || []);
        const pendingCount = allPayments.filter(p => p.status === 'refund_pending').length;
        setPendingRefundsCount(pendingCount);
      }
      if (recognizedRes.success) setRecognizedInstructors(recognizedRes.data || []);

      setStats({
        totalStudents: studentsRes.data?.length || 0,
        totalInstructors: instructorsRes.data?.length || 0,
        totalCourses: coursesRes.data?.length || 0,
        activeCourses: coursesRes.data?.filter(c => c.isPublished === true).length || 0,
        recognizedInstructors: recognizedRes.data?.length || 0
      });
      
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      setError('Failed to load dashboard data. Please try refreshing.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecentActivities = async () => {
    setActivitiesLoading(true);
    try {
      // Fetch admin activities from audit log using the authenticated user's ID
      const response = await getUserActivity(authData.id, { limit: 10 });

      if (response.success) {
        const formattedActivities = formatActivities(response.data);
        setRecentActivities(formattedActivities);
      }
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  // ✅ FORMAT ACTIVITIES FOR DISPLAY
  const formatActivities = (activities) => {
    return activities.map(activity => {
      const icon = getActivityIcon(activity.action);
      const description = getDescription(activity);
      const timeAgo = getTimeAgo(new Date(activity.timestamp));

      return {
        icon,
        title: getActivityTitle(activity.action),
        description,
        timeAgo,
        timestamp: activity.timestamp
      };
    });
  };

  // ✅ GET ICON BASED ON ACTION TYPE
  const getActivityIcon = (action) => {
    const iconMap = {
      'CREATE_COURSE': '➕',
      'PUBLISH_COURSE': '🚀',
      'UNPUBLISH_COURSE': '⏸️',
      'UPDATE_COURSE': '📝',
      'DELETE_COURSE': '🗑️',
      'VERIFY_INSTRUCTOR': '✅',
      'UPDATE_RECOGNIZED_INSTRUCTOR': '📝',
      'REMOVE_RECOGNIZED_INSTRUCTOR': '❌',
      'CREATE_COURSE_CATEGORY': '📁',
      'UPDATE_CATEGORY': '✏️',
      'DELETE_CATEGORY': '🗂️',
      'ASSIGN_COURSE': '🔗',
      'UNASSIGN_COURSE': '🔓',
    };
    return iconMap[action] || '📋';
  };

  // ✅ GET TITLE BASED ON ACTION TYPE
  const getActivityTitle = (action) => {
    const titleMap = {
      'CREATE_COURSE': 'Course created',
      'PUBLISH_COURSE': 'Course published',
      'UNPUBLISH_COURSE': 'Course unpublished',
      'UPDATE_COURSE': 'Course updated',
      'DELETE_COURSE': 'Course deleted',
      'VERIFY_INSTRUCTOR': 'Instructor verified',
      'UPDATE_RECOGNIZED_INSTRUCTOR': 'Instructor updated',
      'REMOVE_RECOGNIZED_INSTRUCTOR': 'Instructor removed',
      'CREATE_CATEGORY': 'Category created',
      'UPDATE_CATEGORY': 'Category updated',
      'DELETE_CATEGORY': 'Category deleted',
      'ASSIGN_COURSE': 'Course assigned',
      'UNASSIGN_COURSE': 'Course unassigned',
    };
    return titleMap[action] || 'Activity';
  };

  // ✅ GET DEFAULT DESCRIPTION IF actionDescription IS MISSING
  const getDescription = (activity) => {
    // Use actionDescription if available
    if (activity.actionDescription) {
      return activity.actionDescription;
    }
    
    const metadata = activity.metadata || {};
    const entityId = activity.entityId || 'Unknown';
    const entityType = activity.entityType || '';
    
    // Build description from changes array
    if (activity.changes && activity.changes.length > 0) {
      const change = activity.changes[0];
      
      switch(activity.action) {
        // ====================================================================
        // ADMIN-ONLY ACTIONS
        // ====================================================================
        
        // Course management actions
        case 'CREATE_COURSE':
          return `Created course: ${change.newValue || entityId}`;
        
        case 'UPDATE_COURSE':
          return `Updated course ${entityId}: ${activity.changes.map(c => c.field).join(', ')}`;
        
        case 'DELETE_COURSE':
          return `Deleted course: ${change.oldValue || entityId}`;
        
        case 'PUBLISH_COURSE':
          return `Published course: ${entityId}`;
        
        case 'UNPUBLISH_COURSE':
          return `Unpublished course: ${entityId}`;
        
        // Category management actions
        case 'CREATE_CATEGORY':
          return `Created category: ${change.newValue || entityId}`;
        
        case 'UPDATE_CATEGORY':
          return `Renamed category: ${change.oldValue} → ${change.newValue}`;
        
        case 'DELETE_CATEGORY':
          return `Deleted category: ${change.oldValue || entityId}`;
        
        // Instructor management actions
        case 'VERIFY_INSTRUCTOR':
          return `Verified instructor: ${entityId}`;
        
        case 'UPDATE_RECOGNIZED_INSTRUCTOR':
          return `Updated recognized instructor: ${entityId}`;
        
        case 'REMOVE_RECOGNIZED_INSTRUCTOR':
          return `Removed recognized instructor: ${entityId}`;
        
        case 'ASSIGN_COURSE':
          return `Assigned course ${change.newValue} to instructor ${entityId}`;
        
        case 'UNASSIGN_COURSE':
          return `Unassigned course ${change.oldValue} from instructor ${entityId}`;
        
        // ====================================================================
        // INSTRUCTOR ACTIONS (can appear in admin dashboard)
        // ====================================================================
        
        // Instructor grading actions
        case 'GRADE_LAB':
          return `Graded lab for student ${entityId}${metadata.courseId ? ` in ${metadata.courseId}` : ''}${metadata.labId ? ` - ${metadata.labId}` : ''}`;
        
        case 'GRADE_QUIZ':
          return `Graded quiz for student ${entityId}${metadata.courseId ? ` in ${metadata.courseId}` : ''}${metadata.quizId ? ` - ${metadata.quizId}` : ''}`;
        
        case 'GRADE_EXAM':
          return `Graded exam for student ${entityId}${metadata.courseId ? ` in ${metadata.courseId}` : ''}${metadata.examId ? ` - ${metadata.examId}` : ''}`;
        
        case 'GRADE_CAPSTONE':
          return `Graded capstone for student ${entityId}${metadata.courseId ? ` in ${metadata.courseId}` : ''}`;
        
        // Instructor profile actions
        case 'CREATE_INSTRUCTOR':
          return `Created instructor account: ${entityId}`;
        
        case 'UPDATE_INSTRUCTOR':
          return `Updated instructor profile: ${activity.changes.map(c => c.field).join(', ')}`;
        
        // Instructor course content actions
        case 'ADD_LESSON':
          return `Added lesson to course ${metadata.courseId || entityId}`;
        
        case 'ADD_LAB':
          return `Added lab to course ${metadata.courseId || entityId}`;
        
        case 'ADD_QUIZ':
          return `Added quiz to course ${metadata.courseId || entityId}`;
        
        case 'ADD_EXAM':
          return `Added exam to course ${metadata.courseId || entityId}`;
        
        case 'ADD_CAPSTONE':
          return `Added capstone to course ${metadata.courseId || entityId}`;
        
        case 'REMOVE_LESSON':
          return `Removed lesson from course ${metadata.courseId || entityId}`;
        
        case 'REMOVE_LAB':
          return `Removed lab from course ${metadata.courseId || entityId}`;
        
        case 'REMOVE_QUIZ':
          return `Removed quiz from course ${metadata.courseId || entityId}`;
        
        case 'REMOVE_EXAM':
          return `Removed exam from course ${metadata.courseId || entityId}`;
        
        case 'REMOVE_CAPSTONE':
          return `Removed capstone from course ${metadata.courseId || entityId}`;
        
        // ====================================================================
        // STUDENT ACTIONS (can appear in admin dashboard)
        // ====================================================================
        
        case 'SUBMIT_LAB':
          return `Student ${entityId} submitted lab in ${metadata.courseId || 'course'}`;
        
        case 'SUBMIT_QUIZ':
          return `Student ${entityId} submitted quiz in ${metadata.courseId || 'course'}`;
        
        case 'SUBMIT_EXAM':
          return `Student ${entityId} submitted exam in ${metadata.courseId || 'course'}`;
        
        case 'SUBMIT_CAPSTONE':
          return `Student ${entityId} submitted capstone`;
        
        case 'ENROLL':
          return `Student ${entityId} enrolled in course ${change.newValue}`;
        
        case 'UNENROLL':
          return `Student ${entityId} unenrolled from course ${change.oldValue}`;
        
        case 'ADD_TO_CART':
          return `Student ${entityId} added course ${change.newValue} to cart`;
        
        case 'REMOVE_FROM_CART':
          return `Student ${entityId} removed course ${change.oldValue} from cart`;
        
        case 'PAYMENT':
          return `Student ${entityId} completed payment`;
        
        case 'CALCULATE_GRADE':
          return `Grade calculated for student ${entityId}`;
        
        case 'COMPLETE_LESSON':
          return `Student ${entityId} completed lesson`;
        
        // ====================================================================
        // GENERIC ACTIONS
        // ====================================================================
        
        case 'CREATE':
          if (entityType === 'student') {
            return `Created student account: ${entityId}`;
          }
          return change.newValue || `Created ${entityType} ${entityId}`;
        
        case 'UPDATE':
          if (entityType === 'student') {
            return `Updated student ${entityId}: ${activity.changes.map(c => c.field).join(', ')}`;
          }
          return `Updated ${change.field}`;
        
        case 'DELETE':
          if (entityType === 'student') {
            return `Deleted student: ${entityId}`;
          }
          if (entityType === 'instructor') {
            return `Deleted instructor: ${entityId}`;
          }
          return `Deleted ${entityType}: ${change.oldValue || entityId}`;
        
        case 'SUBMIT':
          return change.newValue || `Submitted to ${entityType} ${entityId}`;
        
        case 'READ':
          return change.newValue || `Downloaded file`;
        
        case 'GRADE':
          return change.newValue || `Graded ${entityType} ${entityId}`;
        
        // File management actions
        case 'UPLOAD':
          return change.newValue || `Uploaded file`;
        
        case 'DOWNLOAD':
          return change.newValue || `Downloaded file`;
        
        case 'DELETE_FILE':
          return change.oldValue || `Deleted file`;
        
        case 'BULK_DELETE':
          return `Bulk deleted files`;
        
        case 'VIEW':
          return change.newValue || `Viewed ${entityType} ${entityId}`;
        
        // System actions
        case 'LOGIN':
          return `Logged in`;
        
        case 'LOGOUT':
          return `Logged out`;
        
        case 'LOGIN_FAILED':
          return `Failed login attempt for ${entityId}`;
        
        default:
          // Generic fallback
          return `${change.field}: ${change.newValue || change.oldValue}`;
      }
    }
    
    // Final fallback
    return `${activity.action}`;
  };

  // ✅ CALCULATE TIME AGO
  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }
    
    return 'just now';
  };

  const profileHeaderData = { 
      ...authData, 
      // title: '🛠️ Admin Dashboard',
      // subTitle: `Welcome, ${authData?.name}`
  };

  return (
    <div className="main-container">
      <div className="main-content">
        
        {/* Header */}
        <div className="header-row">
          <ProfileHeader 
            data={profileHeaderData} 
            activePage="admin/dashboard" 
          />
          <div className="headerMiddle">
            <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
          </div>
          <SharedHeader 
            notificationCount={0} 
            icons={[]} 
          />
        </div>

        {/* Toolbar */}
        <div className="ai-notebook-toolbar">
          <div className="ai-notebook-toolbar-left">
            <BackButton 
              to="-1"
              activeTab="management"
              label="←"
              bgColor="#6b7280"
            />
            <span className="ai-notebook-flow-name">
              <span className="ai-notebook-lab-tag">🛠️ Admin Dashboard</span>
              {/* <span className="ai-notebook-session-id">📚 Course: {courseInfo?.title} ({courseInfo?.courseId})</span> */}
            </span>
          </div>
          {/* <div className="ai-notebook-toolbar-right">
          </div> */}
        </div>

        {/* Tab Navigation */}
        <div className="top-tab-container">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 Overview
          </button>
          <button
            className={`tab-button ${activeTab === 'management' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('management')}
          >
            🗂️ Management
          </button>

        </div>

        {/* Content Area */}
        {isLoading ? (
          <div className="loading-container">
            <div className="loader"></div>
            <p>Loading dashboard...</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="scrollable-list">

                {/* Error Display */}
                {error && (
                  <div className="search-container">
                    <div style={{ 
                      padding: '15px', 
                      background: '#fee', 
                      color: '#c33', 
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      ⚠️ {error}
                    </div>
                  </div>
                )}

                {/* Statistics Cards */}
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon">👨‍🎓</div>
                    <div className="stat-info">
                      <h3>{stats.totalStudents}</h3>
                      <p>Total Students</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">👨‍🏫</div>
                    <div className="stat-info">
                      <h3>{stats.totalInstructors}</h3>
                      <p>Total Instructors</p>
                    </div>
                    <div className="stat-info">
                      <h3>{stats.recognizedInstructors}</h3>
                      <p>Verified Instructors</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">📚</div>
                    <div className="stat-info">
                      <h3>{stats.totalCourses}</h3>
                      <p>Total Courses</p>
                    </div>
                    <div className="stat-info">
                      <h3>{stats.activeCourses}</h3>
                      <p>Published Courses</p>
                    </div>
                  </div>
                </div>

                {/* ✅ REAL ACTIVITIES SECTION */}
                <div className="recent-activity">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3>📋 Recent Platform Activity</h3>
                    <button 
                      onClick={loadRecentActivities}
                      style={{
                        padding: '8px 16px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      🔄 Refresh
                    </button>
                  </div>
                  
                  {activitiesLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <div className="loader"></div>
                      <p>Loading activities...</p>
                    </div>
                  ) : recentActivities.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
                      <p>No recent activities found</p>
                    </div>
                  ) : (
                    <div className="activity-list">
                      {recentActivities.map((activity, index) => (
                        <div key={index} className="activity-item">
                          <span className="activity-icon">{activity.icon}</span>
                          <div className="activity-details">
                            <p><strong>{activity.title}:</strong> {activity.description}</p>
                            <span className="activity-time">{activity.timeAgo}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Management Tab */}
            {activeTab === 'management' && (
              <div className="instructor-courses">
                <div className="scrollable-list">

                  {/* Course Management */}
                  <div className="admin-general-card" onClick={() => handleNavigateToManagement('courses')}>
                    <div className="badge-container">
                      <div className="unread-badge">{stats.totalCourses}</div>
                    </div>
                    <div className="general-card-info">
                      <span style={{ fontSize: '48px' }}>📚</span>
                      <h3>Course Management</h3>
                      <p className="expertise">Create, edit, and publish courses</p>
                      <p className="expertise">{stats.activeCourses} active courses</p>
                    </div>
                  </div>

                  {/* Instructor Management */}
                  <div className="admin-general-card" onClick={() => handleNavigateToManagement('instructors')}>
                    <div className="badge-container" style={{left: '120px'}}>
                      <div className="unread-badge">{stats.totalInstructors}</div>
                    </div>
                    <div className="general-card-info">
                      <span style={{ fontSize: '48px' }}>👨‍🏫</span>
                      <h3>Instructor Management</h3>
                      <p className="expertise">Manage instructors & course assignments</p>
                      <p className="expertise">View instructor profiles and data</p>
                    </div>
                  </div>

                  {/* Student Management */}
                  <div className="admin-general-card" onClick={() => handleNavigateToManagement('students')}>
                    <div className="badge-container">
                      <div className="unread-badge">{stats.totalStudents}</div>
                    </div>
                    <div className="general-card-info">
                      <span style={{ fontSize: '48px' }}>👨‍🎓</span>
                      <h3>Student Management</h3>
                      <p className="expertise">View student progress & information</p>
                      <p className="expertise">Track enrollment and performance</p>
                    </div>
                  </div>

                  {/* Refund Management */}
                  <div className="admin-general-card" onClick={() => handleNavigateToManagement('refunds')}>
                      <div className="badge-container">
                          {pendingRefundsCount > 0 && (
                              <div className="unread-badge" style={{ background: '#e74c3c' }}>
                                  {pendingRefundsCount}
                              </div>
                          )}
                      </div>
                      <div className="general-card-info">
                          <span style={{ fontSize: '48px' }}>💰</span>
                          <h3>Refund Management</h3>
                          <p className="expertise">Review and process refund requests</p>
                          <p className="expertise">
                              {pendingRefundsCount > 0 
                                  ? `${pendingRefundsCount} pending refund${pendingRefundsCount > 1 ? 's' : ''}` 
                                  : 'No pending refunds'}
                          </p>
                      </div>
                  </div>

                  {/* Messaging & Communication */}
                  <div className="admin-general-card" onClick={() => handleNavigateToManagement('messaging')}>
                    <div className="badge-container">
                      <div className="new-badge">NEW</div>
                    </div>

                    <div className="general-card-info">
                      <span style={{ fontSize: '48px' }}>💬</span>
                      <h3>Messaging System</h3>
                      <p className="expertise">Send announcements & messages</p>
                      <p className="expertise">Communicate with platform users</p>
                    </div>

                  </div>

                  {/* Configuration */}
                  <div className="admin-general-card" onClick={() => handleNavigateToManagement('configuration')}>
                    <div className="general-card-info">
                      <span style={{ fontSize: '48px' }}>⚙️</span>
                      <h3>Configuration</h3>
                      <p className="expertise">Prof-iX knowledge base & FCM tokens</p>
                      <p className="expertise">Reindex content and manage notifications</p>
                    </div>
                  </div>

                </div>
              </div>
            )}

          </>
        )}

      </div>
    </div>
  );
};

export default AdminDashboard;